/* ---------------------------------------------------------------------------
   Measuring a defect on a photograph.

   `QC-08`, and the half of it that can be checked without a browser.

   WHAT THIS IS AND IS NOT. It is an ASSIST: the inspector marks something of
   known size in the frame, then traces the defect, and the arithmetic below
   turns pixels into millimetres. It is not automatic detection — there is no
   model here, nothing is segmented, and nothing is written to the record without
   somebody choosing it.

   That was a decision, not a shortfall, and the reason is in the arithmetic this
   file makes checkable. Reliable width measurement needs roughly five pixels
   across the feature. DoR's decisive crack threshold is 0.3 mm, so a photograph
   has to resolve about 0.06 mm per pixel — a 12-megapixel phone at roughly 25 to
   35 cm from the surface with the crack filling the frame. Published work that
   claims sub-millimetre accuracy does it with laser or LiDAR scale, or with a
   printed target, under controlled capture. One-handed above a river in the
   monsoon is not controlled capture, and a register that reported 0.28 mm from a
   photograph taken at arm's length would be inventing the one number the
   guideline's rating bands turn on.

   So the tool measures what a photograph can honestly measure — lengths and
   areas, where 10% relative error on 2.4 m² is acceptable — and REFUSES a
   measurement the image cannot resolve, saying so with the arithmetic. See
   `resolutionVerdict`.

   NOTHING HERE TOUCHES THE DOM. The picking is in src/screens/measureDialog.ts;
   what is here is the geometry, so the rules that matter can run in CI. Fourth
   time this repository has taken that trade — see the Decisions in ROADMAP.md.
   --------------------------------------------------------------------------- */

/**
 * A point on the photograph, normalised to `0..1` in each axis.
 *
 * Normalised rather than in pixels because the picking happens against a
 * displayed image whose size depends on the viewport, and storing display pixels
 * would make a measurement mean something different after a rotation. Every
 * distance below multiplies back up by the image's own natural dimensions, which
 * is the only frame in which the arithmetic is true.
 */
export interface PhotoPoint {
  x: number;
  y: number;
}

export interface PhotoScale {
  /** The two ends of the reference, as marked. */
  a: PhotoPoint;
  b: PhotoPoint;
  /** How long the reference really is, in millimetres. */
  lengthMm: number;
  /** The photograph's natural pixel dimensions. */
  width: number;
  height: number;
}

/** Distance between two normalised points, in the image's own pixels. */
export function pixelDistance(a: PhotoPoint, b: PhotoPoint, width: number, height: number): number {
  /* Scaled per axis before the hypotenuse, not after. A normalised distance is
     aspect-distorted — 0.1 across a 4000 px width is four times 0.1 across a
     1000 px height — and treating it as isotropic would put a consistent error
     into every diagonal measurement while leaving horizontal ones correct, which
     is the shape of bug that survives a spot check. */
  const dx = (b.x - a.x) * width;
  const dy = (b.y - a.y) * height;
  return Math.hypot(dx, dy);
}

/**
 * Millimetres per image pixel, or null where the scale is unusable.
 *
 * Null for a reference of no length, a reference marked as a single point, or
 * non-finite dimensions. Every one of those would divide by zero and produce
 * `Infinity`, which formats as a number and would reach the record as one.
 */
export function mmPerPixel(scale: PhotoScale): number | null {
  if (!Number.isFinite(scale.lengthMm) || scale.lengthMm <= 0) return null;
  if (!Number.isFinite(scale.width) || !Number.isFinite(scale.height)) return null;
  if (scale.width <= 0 || scale.height <= 0) return null;

  const px = pixelDistance(scale.a, scale.b, scale.width, scale.height);
  if (!Number.isFinite(px) || px <= 0) return null;

  return scale.lengthMm / px;
}

/**
 * Traced length, in millimetres. Null where the scale is unusable.
 *
 * A polyline rather than a straight line, because a crack is not straight and
 * measuring the chord between its ends understates it — sometimes by a lot on a
 * map-cracked slab, which is exactly the case where the length matters for
 * costing a surface treatment.
 */
export function tracedLengthMm(points: readonly PhotoPoint[], scale: PhotoScale): number | null {
  const perPx = mmPerPixel(scale);
  if (perPx === null || points.length < 2) return null;

  let px = 0;
  for (let i = 1; i < points.length; i++) {
    px += pixelDistance(points[i - 1]!, points[i]!, scale.width, scale.height);
  }
  return px * perPx;
}

/**
 * Traced area, in square millimetres. Null where the scale is unusable.
 *
 * The shoelace formula over the closed polygon, absolute — so it does not matter
 * whether the inspector traced clockwise or anticlockwise, and a self-crossing
 * trace produces a smaller area rather than a negative one.
 *
 * NOT A PROJECTION CORRECTION. This is the area of the shape on the image plane,
 * scaled by one number, which is right only where the surface is roughly
 * perpendicular to the camera and flat. A spall photographed at forty degrees to
 * a pier face reads small, and no amount of arithmetic on a single uncalibrated
 * photograph can recover that — recovering it needs the camera's intrinsics or a
 * planar target with four known corners. The dialog says so where the inspector
 * can read it; this function does not pretend otherwise.
 */
export function tracedAreaMm2(points: readonly PhotoPoint[], scale: PhotoScale): number | null {
  const perPx = mmPerPixel(scale);
  if (perPx === null || points.length < 3) return null;

  let twiceArea = 0;
  for (let i = 0; i < points.length; i++) {
    const p = points[i]!;
    const q = points[(i + 1) % points.length]!;
    twiceArea += p.x * scale.width * (q.y * scale.height) - q.x * scale.width * (p.y * scale.height);
  }
  const areaPx = Math.abs(twiceArea) / 2;
  return areaPx * perPx * perPx;
}

/* --------------------------- what the image can resolve --------------------------- */

/**
 * How many pixels a feature must span before it is worth measuring.
 *
 * Five. Below that, published work reports measurement accuracy falling away
 * sharply — a feature two pixels wide is a feature whose edges are both
 * anti-aliased guesses. This is the number that decides whether a photograph may
 * report a crack width at all.
 */
export const MIN_PIXELS_ACROSS = 5;

export interface ResolutionVerdict {
  /** Whether a feature of this size can honestly be measured on this photograph. */
  ok: boolean;
  /** Millimetres per pixel of the photograph, as scaled. */
  mmPerPx: number;
  /** How many pixels a feature of `targetMm` spans. */
  pixelsAcross: number;
  /** The arithmetic, in words, for the screen. */
  reason: string;
}

/**
 * Whether this photograph can resolve a feature of a given size.
 *
 * THE REFUSAL THAT MAKES THE REST OF THE TOOL HONEST. A traced length of 2 m on
 * a photograph resolving 1 mm per pixel is a fine measurement. A crack width of
 * 0.3 mm on the same photograph is five thousandths of a pixel wide, and every
 * digit of it would be invented by the interpolation. Both come out of the same
 * arithmetic, so nothing distinguishes them except this check.
 *
 * `targetMm` is the size of the thing being measured, not the size of the whole
 * trace: a 2 m crack whose WIDTH is the question is checked against the width.
 */
export function resolutionVerdict(scale: PhotoScale, targetMm: number): ResolutionVerdict | null {
  const mmPerPx = mmPerPixel(scale);
  if (mmPerPx === null || !Number.isFinite(targetMm) || targetMm <= 0) return null;

  const pixelsAcross = targetMm / mmPerPx;
  const ok = pixelsAcross >= MIN_PIXELS_ACROSS;

  const needed = targetMm / MIN_PIXELS_ACROSS;
  return {
    ok,
    mmPerPx,
    pixelsAcross,
    reason: ok
      ? `${round(targetMm)} mm spans about ${Math.round(pixelsAcross)} pixels at ${round(mmPerPx)} mm per pixel.`
      : `${round(targetMm)} mm spans about ${pixelsAcross.toFixed(1)} pixels at ${round(mmPerPx)} mm per pixel, ` +
        `below the ${MIN_PIXELS_ACROSS} a measurement needs. This photograph would have to resolve ` +
        `${round(needed)} mm per pixel — closer to the surface, or a larger sensor.`,
  };
}

/**
 * The size below which this application will not report a width from a photograph.
 *
 * DoR's own decisive threshold: Appendix D separates a Good crack from a Fair one
 * at 0.3 mm. A photograph that cannot resolve 0.3 mm cannot place a crack on the
 * rating scale at all, so reporting a width from it would produce a condition
 * rating out of an invented number.
 */
export const CRITICAL_WIDTH_MM = 0.3;

/** Three significant figures, without exponent notation for small values. */
function round(v: number): string {
  if (v >= 100) return String(Math.round(v));
  if (v >= 1) return String(Number(v.toFixed(2)));
  return String(Number(v.toFixed(4)));
}

/* --------------------------- into the record --------------------------- */

/** Square millimetres to square metres. */
export function mm2ToM2(mm2: number): number {
  return mm2 / 1_000_000;
}

/** Millimetres to metres. */
export function mmToM(mm: number): number {
  return mm / 1000;
}

/**
 * The note stored against a photo-derived measurement, naming its scale source.
 *
 * Goes into `DefectQuantity.instrument`, which is where "how" lives. A
 * photo-derived figure whose scale reference is not recorded cannot be checked
 * by anybody later — and the commonest way one of these is wrong is that the
 * reference was mis-measured or mis-marked, which is exactly what this makes
 * visible.
 */
export function scaleSourceNote(scale: PhotoScale): string {
  const perPx = mmPerPixel(scale);
  const res = perPx === null ? '' : ` (${round(perPx)} mm/px)`;
  return `photo, ${round(scale.lengthMm)} mm reference${res}`;
}
